import { useRef, useState } from 'react';

import {
  dismissGoalsReminder,
  hasCommittedGoalsThisSession,
  isGoalsReminderDismissed,
  markGoalsCommitted,
} from '@/auth/attestationSession';
import { useAuth } from '@/auth/useAuth';
import { Button } from '@/components/ui/Button';

import { GoalsCommitmentModal } from './GoalsCommitmentModal';
import styles from './GoalsReminder.module.css';

/**
 * Non-blocking reminder under the header (PLA-1110): invites the user to
 * review the organization-set goals and commit to them. Never interrupts the
 * page. Hidden for the rest of the session once the user commits or dismisses
 * it; a new login brings it back.
 */
export function GoalsReminder() {
  const { user } = useAuth();
  const accountId = user?.homeAccountId ?? '';
  const [hidden, setHidden] = useState(
    () => hasCommittedGoalsThisSession(accountId) || isGoalsReminderDismissed(accountId),
  );
  const [open, setOpen] = useState(false);
  const [committed, setCommitted] = useState(false);
  const reviewRef = useRef<HTMLButtonElement | null>(null);

  if (hidden) return null;

  const handleClose = () => {
    setOpen(false);
    if (committed) {
      // The banner has done its job; it leaves with the modal.
      setHidden(true);
      return;
    }
    // Return focus to where the user left the page.
    reviewRef.current?.focus();
  };

  const handleCommitted = () => {
    markGoalsCommitted(accountId);
    setCommitted(true);
  };

  return (
    <>
      <aside className={styles.banner} aria-label="Goals reminder">
        <svg
          className={styles.icon}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="5" />
          <circle cx="12" cy="12" r="1" />
        </svg>
        <div className={styles.copy}>
          <strong className={styles.title}>Commit to your goals</strong>
          <span className={styles.detail}>
            Review the documentation targets your organization has set and commit to working
            toward them.
          </span>
        </div>
        <div className={styles.actions}>
          <Button ref={reviewRef} size="sm" onClick={() => setOpen(true)}>
            Review goals
          </Button>
          <button
            type="button"
            className={styles.dismiss}
            onClick={() => {
              dismissGoalsReminder(accountId);
              setHidden(true);
            }}
            aria-label="Dismiss goals reminder until next sign-in"
            title="Dismiss until next sign-in"
          >
            ×
          </button>
        </div>
      </aside>

      {/* Mounted only while open so the goals query does not fire on every page load. */}
      {open && <GoalsCommitmentModal open onClose={handleClose} onCommitted={handleCommitted} />}
    </>
  );
}
